/**
 * Institutional Landing Page Component
 * Dynamically themed landing page for institution-specific subdomains
 */

import React, { useEffect, useState } from 'react';
import { Theme, Loading, InlineNotification, Button, Grid, Column, Tile, Tag } from '@carbon/react';
import { useParams, useNavigate } from 'react-router-dom';
import type { InstitutionConfig, GetInstitutionResponse } from '../../../../shared/interfaces/InstitutionalLanding';

// Marketing components
import MarketingHeader from '../../components/marketing/MarketingHeader';
import MarketingFooter from '../../components/marketing/MarketingFooter';

// Institution-specific components
import InstitutionHeroSection from '../../components/institutions/InstitutionHeroSection';
import InstitutionFeatures from '../../components/institutions/InstitutionFeatures';
import InstitutionTestimonials from '../../components/institutions/InstitutionTestimonials';
import InstitutionCaseStudies from '../../components/institutions/InstitutionCaseStudies';
import InstitutionStatistics from '../../components/institutions/InstitutionStatistics';
import InstitutionCTA from '../../components/institutions/InstitutionCTA';
import InstitutionComplianceSection from '../../components/institutions/InstitutionComplianceSection';

const InstitutionLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [institutionConfig, setInstitutionConfig] = useState<InstitutionConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [themeOverrides, setThemeOverrides] = useState<any>(null);

  useEffect(() => {
    fetchInstitutionConfig();
  }, []);

  const fetchInstitutionConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get subdomain from current URL
      const hostname = window.location.hostname;
      const hostParts = hostname.split('.');
      let subdomain: string | null = null;

      // Extract institution subdomain
      if (hostParts.length >= 2) {
        const possibleSubdomain = hostParts[0];
        const standardSubdomains = ['www', 'api', 'staging', 'production'];

        if (!standardSubdomains.includes(possibleSubdomain)) {
          subdomain = possibleSubdomain;
        }
      }

      if (!subdomain) {
        // No institution subdomain found, redirect to default landing
        navigate('/marketing/landing');
        return;
      }

      // Fetch institution configuration from API
      const apiUrl = import.meta.env.VITE_API_BASE_URL || '/api';
      const response = await fetch(`${apiUrl}/v1/marketing/institutions/by-subdomain?subdomain=${subdomain}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          // Institution not found, redirect to default
          navigate('/marketing/landing');
          return;
        }
        throw new Error('Failed to fetch institution configuration');
      }

      const data: GetInstitutionResponse = await response.json();
      setInstitutionConfig(data.institution);

      // Apply theme overrides based on institution branding
      if (data.institution.branding?.colors) {
        const colors = data.institution.branding.colors;
        setThemeOverrides({
          '--cds-interactive-01': colors.primary,
          '--cds-interactive-02': colors.secondary,
          '--cds-interactive-03': colors.accent,
          '--cds-text-primary': colors.text || '#161616',
          '--cds-background': colors.background || '#ffffff',
        });
      }
    } catch (error) {
      console.error('Error fetching institution config:', error);
      setError('Failed to load institution page. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Apply theme overrides to document
  useEffect(() => {
    if (themeOverrides) {
      Object.entries(themeOverrides).forEach(([key, value]) => {
        document.documentElement.style.setProperty(key, value as string);
      });

      // Cleanup on unmount
      return () => {
        Object.keys(themeOverrides).forEach(key => {
          document.documentElement.style.removeProperty(key);
        });
      };
    }
  }, [themeOverrides]);

  if (loading) {
    return (
      <div className="institution-landing-loading">
        <Loading description="Loading institution page..." />
      </div>
    );
  }

  if (error || !institutionConfig) {
    return (
      <div className="institution-landing-error">
        <InlineNotification kind="error" title="Error" subtitle={error || 'Institution configuration not found'} />
        <Button onClick={() => navigate('/marketing/landing')}>Go to Main Page</Button>
      </div>
    );
  }

  return (
    <div className="institution-landing-page">
      {/* Marketing Header with Institution Branding */}
      <MarketingHeader variant="default" />

      {/* Hero Section with Institution Content */}
      <InstitutionHeroSection
        headline={institutionConfig.content.heroSection.headline}
        subheadline={institutionConfig.content.heroSection.subheadline}
        description={institutionConfig.content.heroSection.description}
        ctaButtons={institutionConfig.content.heroSection.ctaButtons}
        backgroundImage={institutionConfig.content.heroSection.backgroundImage}
        videoUrl={institutionConfig.content.heroSection.videoUrl}
        institutionName={institutionConfig.institutionName}
        institutionType={institutionConfig.institutionType}
      />

      {/* Institution Statistics */}
      {institutionConfig.content.statistics && institutionConfig.content.statistics.length > 0 && (
        <InstitutionStatistics statistics={institutionConfig.content.statistics} />
      )}

      {/* Features Section */}
      {institutionConfig.content.features && institutionConfig.content.features.length > 0 && (
        <InstitutionFeatures features={institutionConfig.content.features} />
      )}

      {/* Testimonials */}
      {institutionConfig.content.testimonials && institutionConfig.content.testimonials.length > 0 && (
        <InstitutionTestimonials
          testimonials={institutionConfig.content.testimonials}
          institutionName={institutionConfig.institutionName}
        />
      )}

      {/* Case Studies */}
      {institutionConfig.content.caseStudies && institutionConfig.content.caseStudies.length > 0 && (
        <InstitutionCaseStudies caseStudies={institutionConfig.content.caseStudies} />
      )}

      {/* Compliance Section */}
      {institutionConfig.content.complianceDocuments && institutionConfig.content.complianceDocuments.length > 0 && (
        <InstitutionComplianceSection
          documents={institutionConfig.content.complianceDocuments}
          institutionName={institutionConfig.institutionName}
        />
      )}

      {/* Role-Based CTAs */}
      <Grid className="institution-role-based-ctas" narrow>
        <Column lg={16}>
          <h2>Get Started Based on Your Role</h2>
          <div className="role-cards">
            {institutionConfig.navigation.roleBasedRoutes.map(route => (
              <Tile key={route.role} className="role-card">
                <h3>{route.role.replace(/_/g, ' ').toUpperCase()}</h3>
                <p>Access your personalized dashboard and resources</p>
                <Button kind="tertiary" size="sm" onClick={() => navigate(route.landingPath)}>
                  Enter as {route.role.replace(/_/g, ' ')}
                </Button>
              </Tile>
            ))}
          </div>
        </Column>
      </Grid>

      {/* Final CTA Section */}
      <InstitutionCTA
        institutionName={institutionConfig.institutionName}
        contactInfo={institutionConfig.content.contactInfo}
        ctaButtons={institutionConfig.content.heroSection.ctaButtons}
      />

      {/* Marketing Footer */}
      <MarketingFooter />

      <style>{`
        .institution-landing-page {
          min-height: 100vh;
          background-color: var(--cds-background, #ffffff);
        }

        .institution-landing-loading,
        .institution-landing-error {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2rem;
        }

        .institution-role-based-ctas {
          padding: var(--cds-spacing-11) 0;
          background-color: var(--cds-layer-01);
        }

        .institution-role-based-ctas h2 {
          margin-bottom: var(--cds-spacing-08);
          font-size: var(--cds-heading-05-font-size);
          font-weight: var(--cds-heading-05-font-weight);
          color: var(--cds-text-primary);
        }

        .role-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: var(--cds-spacing-06);
          margin-top: var(--cds-spacing-08);
        }

        .role-card {
          padding: var(--cds-spacing-06);
          background-color: var(--cds-layer-02);
          transition: transform var(--cds-duration-fast-02) var(--cds-ease-out), box-shadow var(--cds-duration-fast-02) var(--cds-ease-out);
        }

        .role-card:hover {
          transform: translateY(calc(-1 * var(--cds-spacing-02)));
          box-shadow: var(--cds-shadow);
        }

        .role-card h3 {
          font-size: var(--cds-heading-02-font-size);
          font-weight: var(--cds-heading-02-font-weight);
          margin-bottom: var(--cds-spacing-03);
          color: var(--cds-interactive-01);
        }

        .role-card p {
          font-size: var(--cds-body-short-01-font-size);
          color: var(--cds-text-secondary);
          margin-bottom: var(--cds-spacing-05);
        }
      `}</style>
    </div>
  );
};

export default InstitutionLandingPage;
